/* eslint-disable react-hooks/rules-of-hooks */
import { ReactNode, memo, useCallback } from 'react';
import { Formik, FormikConfig, FormikHelpers, FormikProps } from 'formik';
import { Button, Form, Input, message, Modal, ModalProps, Radio } from 'antd';

import FormField from 'components/FormField';
import { useTranslate } from '@tolgee/react';
import { DeleteOutlined } from '@ant-design/icons';
import StatusButton from 'components/StatusButton';

export interface DingTalkFormValues extends Remind.DingTalkConfig {
  type: Remind.OutsiteType;
  taskId: Remind.Config['taskId'];
}

type FV = DingTalkFormValues;

export type DingTalkFormHelpers = FormikHelpers<FV>;

type FormRenderChildren<FV> = (nodes: ReactNode, formProps: FormikProps<FV>) => ReactNode;

export interface DingTalkFormProps extends FormikConfig<FV> {
  children?: ReactNode | FormRenderChildren<FV>;
}

function DingTalkForm(props: DingTalkFormProps) {
  const { children, ...otherProps } = props;

  const { t } = useTranslate();

  const isRenderProps = typeof children === 'function';

  const validate = useCallback(
    (values: FV) => {
      const errors: Partial<Record<keyof typeof values, any>> = {};

      const { secret, webhook } = values;

      if (!secret || !secret.trim()) {
        errors.secret = t('remind.请输入密钥');
      }

      if (!webhook || !webhook.trim()) {
        errors.webhook = t('remind.请输入webhook地址');
      }

      return errors;
    },
    [t],
  );

  return (
    <Formik enableReinitialize validateOnMount validate={validate} {...otherProps}>
      {(props) => {
        const {
          values: { type },
          initialValues,
        } = props;

        const isEdit = Boolean(initialValues.webhook);

        const form = (
          <Form layout="vertical">
            <FormField<FV['type']> name="type" required label={t('remind.选择平台')}>
              {({ field: { value, onChange } }) => {
                return (
                  <div>
                    <Radio
                      disabled={isEdit}
                      checked={value === 0}
                      onChange={({ target: { checked } }) => checked && onChange(0)}
                    >
                      {t('钉钉')}
                    </Radio>
                    <Radio
                      disabled={isEdit}
                      checked={value === 1}
                      onChange={({ target: { checked } }) => checked && onChange(1)}
                    >
                      {t('飞书')}
                    </Radio>
                  </div>
                );
              }}
            </FormField>
            <FormField<FV['secret']>
              name="secret"
              required
              label={t('remind.密钥')}
              description={
                type === 1
                  ? '在飞书群中添加自定义机器人后，勾选「安全设置」中的「签名校验」，即可获得密钥'
                  : undefined
              }
            >
              {({ field }) => {
                return <Input disabled={isEdit} placeholder={t('remind.请输入密钥')} {...field} />;
              }}
            </FormField>
            <FormField<FV['webhook']> name="webhook" required label="webhook">
              {({ field }) => {
                return (
                  <Input disabled={isEdit} placeholder={t('remind.请输入webhook地址')} {...field} />
                );
              }}
            </FormField>
          </Form>
        );

        return isRenderProps ? (children as FormRenderChildren<FV>)(form, props) : form;
      }}
    </Formik>
  );
}

export interface DingTalkFormModalProps
  extends ModalProps,
    Pick<DingTalkFormProps, 'initialValues' | 'onSubmit'> {
  onDelete: () => Promise<any>;
}

function FormModal(props: DingTalkFormModalProps) {
  const { initialValues, onSubmit, onDelete, onCancel, ...otherProps } = props;

  const { t } = useTranslate();

  return (
    <DingTalkForm initialValues={initialValues} onSubmit={onSubmit}>
      {(form, props) => {
        const { isValid, isSubmitting, handleSubmit } = props;

        const isEdit = Boolean(initialValues.webhook);

        const footer = (
          <div>
            {isEdit && (
              <StatusButton
                type="text"
                icon={<DeleteOutlined />}
                onClick={(e) => {
                  return onDelete()
                    .then(() => {
                      if (onCancel) {
                        onCancel(e);
                      }

                      message.success(t('phrase.删除成功'));
                    })
                    .catch(() => {
                      message.error(t('phrase.删除失败，请稍后再试'));
                    });
                }}
                popConfirmProps={{
                  title: t('outside-remind-delete-tips'),
                }}
              >
                {t('phrase.删除')}
              </StatusButton>
            )}
            <Button onClick={onCancel}>{t('phrase.取消')}</Button>
            <Button
              type="primary"
              disabled={!isValid}
              loading={isSubmitting}
              onClick={() => handleSubmit()}
            >
              {t('phrase.保存')}
            </Button>
          </div>
        );

        return (
          <Modal
            width={420}
            title={t(isEdit ? 'knowledge.编辑x' : 'knowledge.新增x', { x: t('remind.站外提醒') })}
            footer={footer}
            onCancel={onCancel}
            {...otherProps}
          >
            {form}
          </Modal>
        );
      }}
    </DingTalkForm>
  );
}

export const DingTalkFormModal = memo(FormModal);

export default memo(DingTalkForm);
